import React, { useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { CA_CATEGORIES } from "../../lib/taxConfig";

const r0 = (n) => Math.round(Number(n) || 0);
const fmt = (n) => r0(n).toLocaleString("en-MY");

// Replicates backend tax_calc.compute_assets for live in-table display.
const computeRow = (row) => {
  const orig = Number(row.originalCost) || 0;
  const cbf = Number(row.costBf) || 0;
  const add = Number(row.addition) || 0;
  const del = Number(row.deletion) || 0;
  const rvBf = Number(row.rvBf) || 0;
  const dv = Number(row.disposalValue) || 0;
  const rateIA = Number(row.rateIA) || 0;
  const rateAA = Number(row.rateAA) || 0;

  const costCf = r0(cbf + add - del);
  const disposed = del > 0 || dv > 0;
  let ia = 0, aa = 0, ba = 0, bc = 0, rvCf = 0;
  if (disposed) {
    const re = rvBf;
    if (dv < re) ba = r0(re - dv);
    else if (dv > re) bc = r0(dv - re);
    rvCf = 0;
  } else {
    const rvBefore = r0(rvBf + add);
    const qeProvided = row.qe !== "" && row.qe !== null && row.qe !== undefined;
    const qe = qeProvided ? (Number(row.qe) || 0) : 0;
    const base = qeProvided ? qe : (orig > 0 ? orig : costCf);
    ia = add > 0 ? r0((rateIA / 100) * base) : 0;
    aa = r0((rateAA / 100) * base);
    let allow = ia + aa;
    if (allow > rvBefore) {
      if (aa > rvBefore) { aa = r0(rvBefore); ia = 0; }
      else { ia = r0(rvBefore - aa); }
    }
    rvCf = r0(Math.max(rvBefore - (ia + aa), 0));
  }
  return { costCf, ia, aa, rvCf, ba, bc };
};

// Column spec. kind: text | num (editable) | calc (read-only) | check (checkbox). sum: include in subtotals.
const COLS = [
  { key: "category", label: "Category", kind: "text", w: "min-w-[120px]" },
  { key: "description", label: "Description", kind: "text", w: "min-w-[150px]" },
  { key: "yearAcq", label: "Yr Acq", kind: "text", w: "w-16" },
  { key: "rateIA", label: "IA%", kind: "num", w: "w-14" },
  { key: "rateAA", label: "AA%", kind: "num", w: "w-14" },
  { key: "originalCost", label: "Orig. Cost", kind: "num", w: "w-24", sum: true },
  { key: "qe", label: "QE (Qualifying Exp.)", kind: "num", w: "w-28", sum: true },
  { key: "costBf", label: "Cost b/f", kind: "num", w: "w-24", sum: true },
  { key: "addition", label: "Addition", kind: "num", w: "w-24", sum: true },
  { key: "deletion", label: "Deletion", kind: "num", w: "w-24", sum: true },
  { key: "costCf", label: "Cost c/f", kind: "calc", w: "w-24", sum: true },
  { key: "rvBf", label: "R.V. b/f", kind: "num", w: "w-24", sum: true },
  { key: "ia", label: "Init. Allow", kind: "calc", w: "w-24", sum: true },
  { key: "aa", label: "Ann. Allow", kind: "calc", w: "w-24", sum: true },
  { key: "inUse", label: "In Use", kind: "check", w: "w-20" },
  { key: "notional", label: "Not. Allow", kind: "check", w: "w-20" },
  { key: "rvCf", label: "R.V. c/f", kind: "calc", w: "w-24", sum: true },
  { key: "disposalValue", label: "Disposal Price", kind: "num", w: "w-24", sum: true },
  { key: "ba", label: "BA", kind: "calc", w: "w-20", sum: true },
  { key: "bc", label: "BC", kind: "calc", w: "w-20", sum: true },
];

const cellNum = (col, row, calc) => (col.kind === "calc" ? calc[col.key] : (Number(row[col.key]) || 0));

// Full-precision version of computeRow (no per-asset rounding) — used ONLY for column
// subtotals/grand total so they round the COLUMN (matching the backend engine), instead of
// summing already-rounded per-asset values (which can drift a ringgit or two).
const computeRowExact = (row) => {
  const orig = Number(row.originalCost) || 0;
  const cbf = Number(row.costBf) || 0;
  const add = Number(row.addition) || 0;
  const del = Number(row.deletion) || 0;
  const rvBf = Number(row.rvBf) || 0;
  const dv = Number(row.disposalValue) || 0;
  const rateIA = Number(row.rateIA) || 0;
  const rateAA = Number(row.rateAA) || 0;
  const costCf = cbf + add - del;
  const disposed = del > 0 || dv > 0;
  let ia = 0, aa = 0, ba = 0, bc = 0, rvCf = 0;
  if (disposed) {
    const re = rvBf;
    if (dv < re) ba = re - dv;
    else if (dv > re) bc = dv - re;
    rvCf = 0;
  } else {
    const rvBefore = rvBf + add;
    const qeProvided = row.qe !== "" && row.qe !== null && row.qe !== undefined;
    const qe = qeProvided ? (Number(row.qe) || 0) : 0;
    const base = qeProvided ? qe : (orig > 0 ? orig : costCf);
    ia = add > 0 ? (rateIA / 100) * base : 0;
    aa = (rateAA / 100) * base;
    if (ia + aa > rvBefore) {
      if (aa > rvBefore) { aa = rvBefore; ia = 0; }
      else { ia = rvBefore - aa; }
    }
    rvCf = Math.max(rvBefore - (ia + aa), 0);
  }
  return { costCf, ia, aa, rvCf, ba, bc };
};
const cellNumExact = (col, row, calc) => (col.kind === "calc" ? calc[col.key] : (Number(row[col.key]) || 0));

// Largest-remainder rounding so whole-ringgit IA/AA line items sum EXACTLY to the rounded column
// total (the CA schedule "casts"). Matches the backend _apportion_round.
const apportionRound = (values) => {
  const target = Math.round(values.reduce((s, v) => s + v, 0));
  const floors = values.map((v) => Math.floor(v));
  const res = floors.slice();
  const rem = target - floors.reduce((s, v) => s + v, 0);
  if (rem > 0) {
    const order = values.map((v, i) => [i, v - floors[i]]).sort((a, b) => b[1] - a[1]);
    for (let k = 0; k < Math.min(rem, order.length); k++) res[order[k][0]] += 1;
  }
  return res;
};

// Capital allowance asset schedule: grouped by category with subtotals + grand total.
const AssetRows = ({ rows, onChange }) => {
  const [editCat, setEditCat] = useState(null);
  const [editVal, setEditVal] = useState("");
  // Rename a whole category (label only) across every asset in that group.
  const renameGroup = (oldCat, newRaw) => {
    const newCat = String(newRaw || "").trim();
    setEditCat(null);
    if (!newCat || newCat === oldCat) return;
    onChange(rows.map((r) => {
      const cat = (r.category || "").trim() || "(Uncategorised)";
      return cat === oldCat ? { ...r, category: newCat } : r;
    }));
  };
  const update = (idx, field, value) => onChange(rows.map((r, i) => {
    if (i !== idx) return r;
    const next = { ...r, [field]: value };
    if (field === "inUse") next.notional = !value; // asset not used in business -> notional allowance
    if (field === "category") {
      const match = CA_CATEGORIES.find((c) => c.label.toLowerCase() === String(value).trim().toLowerCase());
      if (match) { next.rateIA = match.rateIA; next.rateAA = match.rateAA; }
    }
    return next;
  }));
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const add = () => onChange([...rows, { description: "", category: "", yearAcq: "", rateIA: 20, rateAA: 20, originalCost: 0, qe: "", costBf: 0, addition: 0, deletion: 0, disposalValue: 0, inUse: true, notional: false, rvBf: 0 }]);

  // Group rows by category preserving first-appearance order (keep real index for edits)
  const groups = [];
  const byCat = {};
  rows.forEach((row, idx) => {
    const cat = (row.category || "").trim() || "(Uncategorised)";
    if (!(cat in byCat)) { byCat[cat] = groups.length; groups.push({ category: cat, items: [] }); }
    groups[byCat[cat]].items.push({ row, idx });
  });

  // Apportioned whole-ringgit IA/AA by row index so the schedule casts (line items add to totals).
  const apIA = apportionRound(rows.map((r) => computeRowExact(r).ia));
  const apAA = apportionRound(rows.map((r) => computeRowExact(r).aa));
  // R.V. c/f reconciled to the ROUNDED IA/AA (R.V. c/f = R.V. b/f + Addition - IA - AA).
  const rvcfOf = (row, idx) => {
    const disposed = (Number(row.deletion) || 0) > 0 || (Number(row.disposalValue) || 0) > 0;
    if (disposed) return computeRowExact(row).rvCf;
    return Math.max((Number(row.rvBf) || 0) + (Number(row.addition) || 0) - apIA[idx] - apAA[idx], 0);
  };

  const sumOver = (items) => {
    const acc = {};
    COLS.forEach((c) => { if (c.sum) acc[c.key] = 0; });
    items.forEach(({ row, idx }) => {
      const calc = computeRowExact(row);
      COLS.forEach((c) => {
        if (!c.sum) return;
        if (c.key === "ia") acc.ia += apIA[idx];
        else if (c.key === "aa") acc.aa += apAA[idx];
        else if (c.key === "rvCf") acc.rvCf += rvcfOf(row, idx);
        else acc[c.key] += cellNumExact(c, row, calc);
      });
    });
    return acc;
  };
  const grand = sumOver(rows.map((row, idx) => ({ row, idx })));

  const totalRow = (label, sums, key, strong) => (
    <tr key={key} className={strong ? "bg-[#eef4fb]" : "bg-[#f7f9fc]"}>
      {COLS.map((c, ci) => {
        if (ci === 0) {
          return (
            <td key={c.key} className={`px-2 py-1.5 font-semibold whitespace-nowrap border border-gray-300 sticky left-0 z-10 ${strong ? "bg-[#eef4fb] text-[#10416b]" : "bg-[#f7f9fc] text-gray-700"}`}>{label}</td>
          );
        }
        return (
          <td key={c.key} className="px-2 py-1.5 text-right font-mono border border-gray-300">
            {c.sum ? <span className={`font-semibold ${strong ? "text-[#10416b]" : "text-gray-700"}`}>{fmt(sums[c.key])}</span> : ""}
          </td>
        );
      })}
      <td className="border border-gray-300"></td>
    </tr>
  );

  return (
    <div className="border border-gray-300 rounded-sm">
      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-white text-[#1877c9] border-b border-gray-300">
        Schedule of Capital Allowance (assets)
      </div>
      <div className="overflow-auto max-h-[70vh]">
      <table className="w-full min-w-[1760px] text-sm border-collapse">
        <thead>
          <tr className="text-[11px] text-gray-500 bg-[#f7f9fc]">
            {COLS.map((c) => (
              <th key={c.key} className={`px-2 py-1.5 text-left font-medium whitespace-nowrap border border-gray-300 sticky top-0 ${c.kind === "calc" ? "text-[#1877c9]" : ""} ${c.key === "category" ? "left-0 z-30 bg-[#eef4fb]" : "z-20 bg-[#f7f9fc]"}`}>{c.label}</th>
            ))}
            <th className="px-2 border border-gray-300 w-10 sticky top-0 z-20 bg-[#f7f9fc]"></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.category}>
              <tr>
                <td colSpan={COLS.length + 1} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide bg-[#d9e6f5] text-[#10416b] border border-gray-300">
                  {editCat === g.category ? (
                    <input
                      autoFocus
                      value={editVal}
                      onChange={(e) => setEditVal(e.target.value)}
                      onBlur={() => renameGroup(g.category, editVal)}
                      onKeyDown={(e) => { if (e.key === "Enter") renameGroup(g.category, editVal); if (e.key === "Escape") setEditCat(null); }}
                      className="uppercase tracking-wide text-xs font-semibold bg-white border border-[#1877c9] rounded px-1.5 py-0.5 text-[#10416b] min-w-[260px]"
                      data-testid="ca-group-rename-input"
                    />
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      {g.category}
                      <button
                        type="button"
                        onClick={() => { setEditCat(g.category); setEditVal(g.category); }}
                        className="text-[#1877c9] hover:text-[#0f5ea3] opacity-70 hover:opacity-100"
                        title="Rename this category for all its assets"
                        data-testid="ca-group-rename-btn"
                      >
                        <Pencil size={12} />
                      </button>
                    </span>
                  )}
                </td>
              </tr>
              {g.items.map(({ row, idx }) => {
                const calc = computeRow(row);
                return (
                  <tr key={idx}>
                    {COLS.map((c) => {
                      if (c.kind === "calc") {
                        const tone = c.key === "ba" ? "text-green-700" : c.key === "bc" ? "text-amber-700" : "text-gray-700";
                        const highlight = (c.key === "ba" || c.key === "bc") && calc[c.key] > 0;
                        const shown = c.key === "ia" ? apIA[idx] : c.key === "aa" ? apAA[idx] : c.key === "rvCf" ? rvcfOf(row, idx) : calc[c.key];
                        return (
                          <td key={c.key} className="px-2 py-1.5 text-right font-mono bg-[#f9fbfe] border border-gray-300" data-testid={`ca-asset-${idx}-${c.key}`}>
                            <span className={highlight ? `${tone} font-semibold` : "text-gray-600"}>{fmt(shown)}</span>
                          </td>
                        );
                      }
                      if (c.kind === "check") {
                        const checked = c.key === "inUse" ? row.inUse !== false : !!row.notional;
                        return (
                          <td key={c.key} className="px-2 py-1.5 text-center border border-gray-300 bg-[#f9fbfe]">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => update(idx, c.key, e.target.checked)}
                              data-testid={`ca-asset-${idx}-${c.key}`}
                              className="h-4 w-4 accent-[#1877c9] cursor-pointer"
                              title={c.key === "inUse" ? "Untick if the asset was not used in the business during the year" : "Tick if the allowance is notional (deemed, not claimed)"}
                            />
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} className={`p-0 border border-gray-300 ${c.key === "category" ? "sticky left-0 z-10 bg-white" : ""}`}>
                          <input
                            type={c.kind === "num" ? "number" : "text"}
                            value={row[c.key] ?? ""}
                            onChange={(e) => update(idx, c.key, e.target.value)}
                            list={c.key === "category" ? "ca-category-options" : undefined}
                            placeholder={c.key === "qe" && (Number(row.originalCost) || 0) > 0 ? String(row.originalCost) : undefined}
                            data-testid={`ca-asset-${idx}-${c.key}`}
                            className={`${c.w} ${c.kind === "num" ? "text-right font-mono" : ""} w-full text-sm px-2 py-1.5 bg-transparent focus:bg-[#eef4fb] outline-none`}
                          />
                        </td>
                      );
                    })}
                    <td className="px-1 text-center border border-gray-300">
                      <button onClick={() => remove(idx)} data-testid={`ca-asset-${idx}-remove`} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
              {totalRow(`Subtotal — ${g.category}`, sumOver(g.items), `sub-${g.category}`, false)}
            </React.Fragment>
          ))}
          {rows.length > 0 && totalRow("GRAND TOTAL", grand, "grand", true)}
        </tbody>
      </table>
      </div>
      <div className="px-3 py-2 bg-[#f7f9fc] border-t border-gray-300">
        <button onClick={add} data-testid="ca-add-asset-btn" className="flex items-center gap-1 text-sm text-[#1877c9] hover:underline"><Plus size={15} /> Add asset</button>
      </div>
      <datalist id="ca-category-options">
        {CA_CATEGORIES.map((c) => <option key={c.label} value={c.label} />)}
      </datalist>
    </div>
  );
};

export default AssetRows;
